import type { FreePfpResult } from "./freePfpTrayData";

/** Survives AuthModal's post-Google full-page reload. Dual-written to localStorage for email magic-link (new tab). */
export const FREE_PFP_TRAY_STASH_KEY = "pfp_free_tray_stash";
export const PFP_JUST_AUTHENTICATED_KEY = "pfp_just_authenticated";
export const FREE_PFP_DOWNLOAD_AUTH_TRIGGER = "free_pfp_download";
const GATE_DELIVERING_KEY = "pfp_free_tray_gate_delivering";

const STASH_TTL_MS = 24 * 60 * 60 * 1000;
const STASH_VERSION = 1 as const;

export type FreePfpTrayStash = {
  v: typeof STASH_VERSION;
  cutoutUrl: string;
  active: FreePfpResult;
  presetId: string;
  offsetX: number;
  offsetY: number;
  scale: number;
  rotate: number;
  flipH: boolean;
  flipV: boolean;
  pendingDownload: boolean;
  savedAt: number;
};

function isFreePfpResult(value: unknown): value is FreePfpResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.label === "string" &&
    (v.kind === "color" || v.kind === "gradient" || v.kind === "photo")
  );
}

function isStash(value: unknown): value is FreePfpTrayStash {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.v === STASH_VERSION &&
    typeof v.cutoutUrl === "string" &&
    v.cutoutUrl.length > 0 &&
    isFreePfpResult(v.active) &&
    typeof v.presetId === "string" &&
    typeof v.offsetX === "number" &&
    typeof v.offsetY === "number" &&
    typeof v.scale === "number" &&
    typeof v.rotate === "number" &&
    typeof v.flipH === "boolean" &&
    typeof v.flipV === "boolean" &&
    v.pendingDownload === true &&
    typeof v.savedAt === "number"
  );
}

function readStore(storage: Storage): FreePfpTrayStash | null {
  try {
    const raw = storage.getItem(FREE_PFP_TRAY_STASH_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStash(parsed)) return null;
    if (Date.now() - parsed.savedAt > STASH_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStore(storage: Storage, stash: FreePfpTrayStash): boolean {
  try {
    storage.setItem(FREE_PFP_TRAY_STASH_KEY, JSON.stringify(stash));
    return true;
  } catch {
    return false;
  }
}

export function readFreePfpTrayStash(): FreePfpTrayStash | null {
  if (typeof window === "undefined") return null;
  return readStore(window.sessionStorage) ?? readStore(window.localStorage);
}

export function writeFreePfpTrayStash(
  input: Omit<FreePfpTrayStash, "v" | "savedAt" | "pendingDownload">
): boolean {
  if (typeof window === "undefined") return false;
  const stash: FreePfpTrayStash = {
    ...input,
    v: STASH_VERSION,
    pendingDownload: true,
    savedAt: Date.now(),
  };
  const sessionOk = writeStore(window.sessionStorage, stash);
  const localOk = writeStore(window.localStorage, stash);
  return sessionOk || localOk;
}

export function clearFreePfpTrayStash() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(FREE_PFP_TRAY_STASH_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.localStorage.removeItem(FREE_PFP_TRAY_STASH_KEY);
  } catch {
    /* ignore */
  }
}

export function consumeJustAuthenticated(): boolean {
  if (typeof window === "undefined") return false;
  let found = false;
  try {
    if (window.sessionStorage.getItem(PFP_JUST_AUTHENTICATED_KEY) === "true") {
      found = true;
      window.sessionStorage.removeItem(PFP_JUST_AUTHENTICATED_KEY);
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.localStorage.getItem(PFP_JUST_AUTHENTICATED_KEY) === "true") {
      found = true;
      window.localStorage.removeItem(PFP_JUST_AUTHENTICATED_KEY);
    }
  } catch {
    /* ignore */
  }
  return found;
}

export function isGateDelivering(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(GATE_DELIVERING_KEY) === "1";
  } catch {
    return false;
  }
}

export function markGateDelivering() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(GATE_DELIVERING_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearGateDelivering() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(GATE_DELIVERING_KEY);
  } catch {
    /* ignore */
  }
}
